import React, { useRef, useEffect, useState } from "react";
import {
  FaUser,
  FaBirthdayCake,
  FaMapMarkerAlt,
  FaBriefcase,
  FaHeart,
  FaBook,
  FaPrayingHands,
  FaPhoneAlt,
  FaEnvelope,
  FaUsers,
  FaChevronLeft,
  FaChevronRight,
  FaSuitcase,
  FaWallet,
} from "react-icons/fa";
import { LuBook, LuCalendar, LuLanguages, LuMail, LuPhone, LuPointer, LuRuler, LuWallet } from "react-icons/lu";
import ProfileSidebar from "../component/ProfileSidebar";
import { baseurl, imgurl } from "../baseurl";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { getStoredToken } from "../component/utils/getStoredToken";
import ChatButton from "../component/ChatButton";
import CompatibilityCard from "../component/CompatibilityCard";
import InterestButton from "../component/InterestButton";
import FooterTwo from "../component/FooterTwo";
import { FiBriefcase, FiMapPin } from "react-icons/fi";
import NavigateHome from "../component/utils/NavigateHome";
import PageCount from "../component/utils/PageCount";
import ViewProfileMobile from "../component/ViewProfileMobile";

function ViewProfile() {
  const token = getStoredToken();
  const { uid } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [compatibility, setCompatibility] = useState([]);

  const galleryRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  // Logged-in user
  const [loggedUser, setLoggedUser] = useState(null);
  const [loggedGallery, setLoggedGallery] = useState([]);

  const openLightbox = (img) => {
    setCurrentImage(img);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setCurrentImage(null);
  };

  const scrollGallery = (dir) => {
    if (galleryRef.current) {
      galleryRef.current.scrollBy({
        left: dir === "left" ? -300 : 300,
        behavior: "smooth",
      });
    }
  };

  /* --------------------------
      FETCH PROFILE + LOGGED USER
  --------------------------- */
  useEffect(() => {
    const fetchProfile = async () => {
      if (!token || !uid) return;

      try {
        const res = await axios.get(`${baseurl}getUserByUid/${uid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.success) {
          /* ------------------------------
              1️⃣ VIEWED USER ("user")
          ------------------------------- */
          if (res.data.user) {
            const u = res.data.user;

            setProfile({
              name: `${u.first_name} ${u.last_name}`,
              age: u.date_of_birth
                ? Math.floor(
                    (new Date() - new Date(u.date_of_birth)) /
                      (1000 * 60 * 60 * 24 * 365)
                  )
                : "N/A",
              height: u.height
                ? `${(parseFloat(u.height) / 30.48).toFixed(1)}'`
                : "N/A",
              city: u.location || "N/A",
              display_id: u.display_id,
              profession: u.current_occupation || "N/A",
              religion: u.religion || "N/A",
              education: u.degree || u.highest_qualification || "N/A",
              current_occupation: u.current_occupation || "N/A",
              annual_income: u.annual_income || "N/A",
              about: u.about || "No information provided", 
              marital_status: u.marital_status || "N/A",
              location: u.location || "N/A",
              caste: u.caste || "N/A",
              sect: u.sect || "N/A",
              mother_tongue: u.mother_tongue || "N/A",
              family: {
                father: u.father_profession || "",
                mother: u.mother_profession || "",
                siblings: `${u.number_of_siblings || 0} siblings (${
                  u.married_siblings || 0
                } married)`,
              },
              contact: {
                phone: u.phone,
                email: u.email,
              },
              image: u.main_photo ? `${imgurl}${u.main_photo}` : null,
            });

            setGalleryImages(
              u.photos?.length
                ? u.photos.map((p) => `${imgurl}${p}`)
                : u.main_photo
                ? [`${imgurl}${u.main_photo}`]
                : []
            );
          }

          /* ------------------------------
              2️⃣ LOGGED USER ("logged_user")
          ------------------------------- */
          if (res.data.logged_user) {
            const lu = res.data.logged_user;

            setLoggedUser({
              name: `${lu.first_name} ${lu.last_name}`,
              age: lu.date_of_birth
                ? Math.floor(
                    (new Date() - new Date(lu.date_of_birth)) /
                      (1000 * 60 * 60 * 24 * 365)
                  )
                : "N/A",
              height: lu.height
                ? `${(parseFloat(lu.height) / 30.48).toFixed(1)}'`
                : "N/A",
              city: lu.location || "N/A",
              profession: lu.current_occupation || "N/A",
              religion: lu.religion || "N/A",
              education: lu.degree || lu.highest_qualification || "N/A",
              about: lu.about || "",
              image: lu.main_photo ? `${imgurl}${lu.main_photo}` : null,
              photos: lu.photos || [],
            });

            setLoggedGallery(
              lu.photos?.length
                ? lu.photos.map((p) => `${imgurl}${p}`)
                : lu.main_photo
                ? [`${imgurl}${lu.main_photo}`]
                : []
            );
          }

          /* ------------------------------
              3️⃣ COMPATIBILITY
          ------------------------------- */
          if (res.data.compatibility) {
            const comp = Object.entries(res.data.compatibility)
              .filter(([key]) => key !== "overall")
              .map(([key, value]) => ({
                title: key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
                score: value.score,
                reason: value.reason,
              }));

            setCompatibility(comp);
          }
        }
      } catch (err) {
        console.error("Error fetching user profile:", err);
      }
    };

    fetchProfile();
  }, [token, uid]);

  /* ----------------------------
      MASKING PHONE + EMAIL
  ----------------------------- */
  const maskPhone = (phone) => {
    if (!phone) return "N/A";
    const last2 = phone.slice(-2);
    return "XXXXXXXX" + last2;
  };

  const maskEmail = (email) => {
    if (!email) return "N/A";
    const [u, domain] = email.split("@");
    const maskedUser =
      u.length > 2 ? u[0] + "*".repeat(u.length - 2) + u.slice(-1) : u[0] + "*";

    const [dName, dExt] = domain.split(".");
    const maskedDomain =
      dName.length > 2
        ? dName[0] + "*".repeat(dName.length - 2) + dName.slice(-1)
        : dName[0] + "*";

    return `${maskedUser}@${maskedDomain}.${dExt}`;
  };

  if (!profile) return <p className="text-center py-5">Loading...</p>;

  /* ----------------------------
      UI RENDER STARTS HERE
  ----------------------------- */
  return (
    <>
<PageCount pageName = {"Profile Detail Page"}/>
    <NavigateHome/>



<div className="d-block d-lg-none">
  <ViewProfileMobile />
</div>

      <div className="view-profile-page d-none d-lg-block">
        <ProfileSidebar />

        {/* HEADER */}
        <div className="profile-header py-4 px-3 px-md-5">
          <div className="container d-flex flex-column flex-md-row align-items-center gap-4">
            <div className="profile-left d-flex align-items-center gap-3">
              <img
                src={
                  profile.image ||
                  "https://via.placeholder.com/150?text=No+Image"
                }
                alt={profile.name}
                className="profile-photo-sm shadow"
              />
            
            </div>

            {/* BADGES */}
            <div className="profile-right ">
              <div className="mb-2">
                <h4 className=" mb-2 text-white ft-display">
                 ID: {profile.display_id}
                </h4>
                <div className="text-white">
                  <FaMapMarkerAlt size={25}  /> {profile.city} , <FaSuitcase size={25} />{" "}
                  {profile.profession}
                </div>
              </div>

          

              <div>
                <InterestButton uid={uid} />

                <ChatButton uid={uid} />
              </div>
            </div>
          </div>
        </div>

        {/* BASIC INFO */}
        <div className="container py-4">
          <div className="row g-2">
            {/* ABOUT */}
            <div className="col-lg-6">
              <div className="card p-3 ">
                <h6 className=" text-theme mb-2 card-ttl">About</h6>
                <p className="small">
                  <i>"{profile.about}"</i>
                </p>
              </div>
            </div>


            <div className="col-md-6">
              <div className="card p-3  text-capitalize">
                {/* <h6 className=" text-theme mb-2 card-ttl"> Education</h6> */}

                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuRuler size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.height || "N/A"}
                  </div>
                </div>  
<hr/>  
 <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuCalendar size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.age} yrs
                  </div>
                </div> 
                <hr/> 
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <FiMapPin size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.location || "N/A"}
                  </div>
                </div> 
                <hr/> 
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuRuler size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.marital_status || "N/A"}
                  </div>
                </div> 

                <hr/> 
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuBook size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.religion || "N/A"}, {profile.caste || "N/A"}, ({profile.sect || "N/A"}) 
                  </div>
                </div> 

                <hr/> 
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuLanguages size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.mother_tongue || "N/A"}
                  </div>
                </div> 

                <hr/> 
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuWallet size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.annual_income || "N/A"}
                  </div>
                </div> 


              </div>
            </div>

            <div className="col-md-6">
              <div className="card p-3  text-capitalize ">
                <h6 className=" text-theme mb-2 card-ttl"> Career</h6>

                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <FiBriefcase size={25} />
                  </div>
                  <div className="txtcs">
                    {profile.current_occupation || "N/A"}
                  </div>
                </div>
                <hr />
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuWallet size={25} />
                  </div>
                  <div className="txtcs">{profile.annual_income || "N/A"}</div>
                </div>
              </div>
            </div>



            <div className="col-md-6">
              <div className="card p-3   text-capitalize">
                <h6 className=" text-theme mb-2 card-ttl"> Education</h6>

                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <FiBriefcase size={20} />
                  </div>
                  <div className="txtcs">
                    {profile.current_occupation || "N/A"}
                  </div>
                </div> 
              </div>
            </div>

            <div className="col-md-6">
              <div className="card p-3 family-timeline-card  text-capitalize">
  <h6 className="text-theme mb-3 card-ttl">Family</h6>

  <div className="timeline">

    {/* Father */}
    <div className="timeline-item">
      <div className="timeline-dot"></div>

      <div>
        <div className="timeline-label">Father</div>

        <div className="d-flex align-items-center">
          <div className="iconscls me-2">
            <LuPointer size={20} />
          </div>
          <div className="txtcs">
            {profile.family.father || "N/A"}
          </div>
        </div>
      </div>
    </div>

    {/* Mother */}
    <div className="timeline-item">
      <div className="timeline-dot"></div>

      <div>
        <div className="timeline-label">Mother</div>

        <div className="d-flex align-items-center">
          <div className="iconscls me-2">
            <LuPointer size={20} />
          </div>
          <div className="txtcs">
            {profile.family.mother || "N/A"}
          </div>
        </div>
      </div>
    </div>

  </div>
</div>

            </div>


<div className="col-md-6">
              <div className="card p-3  ">
                <h6 className=" text-theme mb-2 card-ttl"> Contact</h6>

                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuPhone size={20} />
                  </div>
                  <div className="txtcs">
                   {maskPhone(profile.contact.phone)}
                  </div>
                </div> 
                <hr/>
                <div className="d-flex align-items-center">
                  <div className="iconscls me-2">
                    <LuMail size={20} />
                  </div>
                  <div className="txtcs">
                  {maskEmail(profile.contact.email)}
                  </div>
                </div> 
              </div>
            </div>

            {/* <div className="col-md-6">
              <div className="card p-3  ">
                <h6 className=" text-theme mb-2 card-ttl"> Family & Contact</h6>

                <ul className="list-unstyled small mb-0">
                  <li>
                    <FaUser /> Father: {profile.family.father || "N/A"}
                  </li>
                  <li>
                    <FaUser /> Mother: {profile.family.mother || "N/A"}
                  </li>
                  <li>
                    <FaUsers /> Siblings: {profile.family.siblings || "N/A"}
                  </li>
                  <hr />
                  <li>
                    <FaPhoneAlt /> {maskPhone(profile.contact.phone)}
                  </li>
                  <li>
                    <FaEnvelope /> {maskEmail(profile.contact.email)}
                  </li>
                </ul>
              </div>
 
            </div> */}
          </div>

          {/* COMPATIBILITY CARD */}
          <div className="row justify-content-center mt-3">
            <div className="col-md-8 col-12">
              <CompatibilityCard
                compatibility={compatibility}
                loggeduser={loggedUser ? loggedUser.photos : []}
                // user={user ? user.photos : []}

                user={galleryImages}
                uid={uid}
                // user={galleryImages}
              />
            </div>
          </div>
        </div>
      </div>

      <FooterTwo />
    </>
  );
}

export default ViewProfile;
